/**
 * FacilitySolve — Session Detail / Interactive Triage Chat
 *
 * Displays the back-and-forth between user and AI for a triage session.
 * User can answer questions, view instructions, resolve, or escalate.
 */

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Button,
	Tag,
	Input,
	message,
	Spin,
	Modal,
	Popconfirm,
} from "antd";
import {
	ArrowLeftOutlined,
	BulbOutlined,
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	SendOutlined,
	ToolOutlined,
	UserOutlined,
	RobotOutlined,
} from "@ant-design/icons";
import {
	getTriageSession,
	submitTriageStep,
	resolveTriageSession,
	escalateTriageSession,
} from "@/services/facility-solve-triage-service";
import type {
	TriageSessionWithSteps,
	TriageStep,
} from "@/services/facility-solve-triage-service";
import "./FacilitySolve.scss";

const FacilitySolveSession = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const chatEndRef = useRef<HTMLDivElement>(null);

	const [session, setSession] = useState<TriageSessionWithSteps | null>(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [userInput, setUserInput] = useState("");
	const [showResolveModal, setShowResolveModal] = useState(false);
	const [resolveSummary, setResolveSummary] = useState("");

	useEffect(() => {
		loadSession();
	}, [id]);

	useEffect(() => {
		chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [session?.steps]);

	const loadSession = async () => {
		if (!id) return;
		setLoading(true);
		try {
			const data = await getTriageSession(parseInt(id));
			setSession(data);
		} catch {
			message.error("Failed to load session");
		} finally {
			setLoading(false);
		}
	};

	const handleSelectOption = async (optionId: string) => {
		if (!session || submitting) return;
		setSubmitting(true);
		try {
			const { userStep, aiStep } = await submitTriageStep(session.id, { selectedOption: optionId });
			setSession((prev) => prev ? { ...prev, steps: [...prev.steps, userStep, aiStep] } : prev);
		} catch (err: any) {
			message.error(err.message || "Failed to process");
		} finally {
			setSubmitting(false);
		}
	};

	const handleSendText = async () => {
		if (!session || !userInput.trim() || submitting) return;
		const text = userInput.trim();
		setUserInput("");
		setSubmitting(true);
		try {
			const { userStep, aiStep } = await submitTriageStep(session.id, { userText: text });
			setSession((prev) => prev ? { ...prev, steps: [...prev.steps, userStep, aiStep] } : prev);
		} catch (err: any) {
			message.error(err.message || "Failed to process");
		} finally {
			setSubmitting(false);
		}
	};

	const handleResolve = async () => {
		if (!session) return;
		try {
			await resolveTriageSession(session.id, resolveSummary || "Issue resolved");
			message.success("Session marked as resolved");
			setShowResolveModal(false);
			loadSession();
		} catch (err: any) {
			message.error(err.message || "Failed to resolve");
		}
	};

	const handleEscalate = async () => {
		if (!session) return;
		try {
			const result = await escalateTriageSession(session.id);
			message.success(`Escalated to work order #${result.workOrderId}`);
			navigate(`/work-orders/${result.workOrderId}`);
		} catch (err: any) {
			message.error(err.message || "Failed to escalate");
		}
	};

	if (loading) {
		return (
			<div className="fst-session-loading">
				<Spin size="large" />
			</div>
		);
	}

	if (!session) {
		return (
			<div className="fst-session-loading">
				<p>Session not found.</p>
				<Button onClick={() => navigate("/work-orders/triage")}>Back to Sessions</Button>
			</div>
		);
	}

	const isActive = session.status === "ACTIVE";
	const lastAiStep = [...session.steps].reverse().find((s) => s.stepType !== "USER_RESPONSE");

	return (
		<div className="fst-session-page">
			<div className="fst-session-header">
				<Button
					type="text"
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate("/work-orders/triage")}
				>
					Back
				</Button>
				<div className="fst-session-title-row">
					<h2>{session.title}</h2>
					<Tag color={session.status === "ACTIVE" ? "processing" : session.status === "RESOLVED_SELF" ? "success" : session.status === "ESCALATED" ? "warning" : "default"}>
						{session.status.replace("_", " ")}
					</Tag>
					{session.urgency && (
						<Tag color={session.urgency === "CRITICAL" ? "red" : session.urgency === "HIGH" ? "orange" : session.urgency === "MEDIUM" ? "blue" : "default"}>
							{session.urgency}
						</Tag>
					)}
				</div>
				{session.category && <Tag style={{ marginTop: 4 }}>{session.category}</Tag>}
				{session.description && <p className="fst-session-desc">{session.description}</p>}
			</div>

			<div className="fst-chat-container">
				{session.steps.map((step, idx) => (
					<StepBubble key={step.id || idx} step={step} />
				))}

				{submitting && (
					<div className="fst-chat-bubble fst-chat-ai">
						<div className="fst-chat-avatar"><RobotOutlined /></div>
						<div className="fst-chat-content">
							<Spin size="small" /> <span style={{ marginLeft: 8, color: "#888" }}>Analyzing...</span>
						</div>
					</div>
				)}

				<div ref={chatEndRef} />
			</div>

			{isActive && (
				<div className="fst-input-bar">
					{lastAiStep?.content?.options && !lastAiStep.content.instructions && !lastAiStep.content.diagnosis ? (
						<div className="fst-options-row">
							{lastAiStep.content.options.map((opt) => (
								<Button
									key={opt.id}
									onClick={() => handleSelectOption(opt.id)}
									disabled={submitting}
									className="fst-option-btn"
								>
									{opt.label}
								</Button>
							))}
						</div>
					) : null}

					<div className="fst-text-input-row">
						<Input
							value={userInput}
							onChange={(e) => setUserInput(e.target.value)}
							onPressEnter={handleSendText}
							placeholder="Type your response or describe the issue further..."
							disabled={submitting}
							size="large"
						/>
						<Button
							type="primary"
							icon={<SendOutlined />}
							onClick={handleSendText}
							disabled={!userInput.trim() || submitting}
							size="large"
						/>
					</div>

					<div className="fst-action-row">
						<Button icon={<CheckCircleOutlined />} onClick={() => setShowResolveModal(true)}>
							Mark as Resolved
						</Button>
						<Popconfirm
							title="Escalate to Work Order?"
							description="This will create a new work order with the triage diagnosis."
							onConfirm={handleEscalate}
						>
							<Button type="primary" danger icon={<ExclamationCircleOutlined />}>
								Escalate to Work Order
							</Button>
						</Popconfirm>
					</div>
				</div>
			)}

			{session.status === "ESCALATED" && session.workOrderId && (
				<div className="fst-escalated-banner">
					<ExclamationCircleOutlined />
					<span>This session was escalated to </span>
					<Button type="link" onClick={() => navigate(`/work-orders/${session.workOrderId}`)}>
						Work Order #{session.workOrderId}
					</Button>
				</div>
			)}

			{session.status === "RESOLVED_SELF" && (
				<div className="fst-resolved-banner">
					<CheckCircleOutlined />
					<span>This issue was self-resolved. {session.resolution?.summary}</span>
				</div>
			)}

			<Modal
				open={showResolveModal}
				onCancel={() => setShowResolveModal(false)}
				title="Mark Issue as Resolved"
				onOk={handleResolve}
				okText="Confirm Resolved"
			>
				<p>How was the issue resolved?</p>
				<Input.TextArea
					value={resolveSummary}
					onChange={(e) => setResolveSummary(e.target.value)}
					placeholder="Describe what fixed the issue..."
					rows={3}
				/>
			</Modal>
		</div>
	);
};

const StepBubble = ({ step }: { step: TriageStep }) => {
	const isUser = step.stepType === "USER_RESPONSE";
	const isEscalation = step.stepType === "ESCALATION";
	const isResolution = step.stepType === "RESOLUTION";

	if (isEscalation || isResolution) {
		return (
			<div className={`fst-chat-system ${isEscalation ? "fst-chat-escalation" : "fst-chat-resolution"}`}>
				{isEscalation ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
				<span>{step.content.text}</span>
			</div>
		);
	}

	return (
		<div className={`fst-chat-bubble ${isUser ? "fst-chat-user" : "fst-chat-ai"}`}>
			<div className="fst-chat-avatar">
				{isUser ? <UserOutlined /> : <RobotOutlined />}
			</div>
			<div className="fst-chat-content">
				{step.content.text && <p className="fst-chat-text">{step.content.text}</p>}

				{step.content.selectedOption && (
					<p className="fst-chat-text">Selected: <strong>{step.content.selectedOption}</strong></p>
				)}

				{step.content.userText && isUser && (
					<p className="fst-chat-text">{step.content.userText}</p>
				)}

				{step.content.instructions && (
					<div className="fst-chat-instructions">
						<div className="fst-instructions-header"><ToolOutlined /> Recommended Steps:</div>
						<ol>
							{step.content.instructions.map((inst, i) => (
								<li key={i}>{inst}</li>
							))}
						</ol>
					</div>
				)}

				{step.content.diagnosis && (
					<div className="fst-chat-diagnosis">
						<div className="fst-diagnosis-header">
							<BulbOutlined /> AI Diagnosis
							<Tag color={step.content.diagnosis.confidence >= 80 ? "success" : step.content.diagnosis.confidence >= 50 ? "warning" : "default"} style={{ marginLeft: 8 }}>
								{step.content.diagnosis.confidence}% confidence
							</Tag>
						</div>
						<p><strong>Root Cause:</strong> {step.content.diagnosis.rootCause}</p>
						{step.content.diagnosis.suggestedActions?.length > 0 && (
							<div>
								<strong>Suggested Actions:</strong>
								<ul>
									{step.content.diagnosis.suggestedActions.map((a, i) => (
										<li key={i}>{a}</li>
									))}
								</ul>
							</div>
						)}
					</div>
				)}

				{!isUser && step.content.options && !step.content.instructions && !step.content.diagnosis && (
					<div className="fst-chat-options-preview">
						{step.content.options.map((opt) => (
							<Tag key={opt.id}>{opt.label}</Tag>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export { FacilitySolveSession };
